/*
 * WebMCP: report Contact Form 7 results to AI agents.
 *
 * Forms carrying a `toolname` attribute (see functions.php) are exposed as WebMCP tools.
 * CF7 replaces the native submit with its own REST request and calls preventDefault(),
 * so the browser needs SubmitEvent.respondWith() to know how the submission ended;
 * without it, the agent receives an error even when the mail was sent.
 *
 * Nothing here stops or replaces CF7's events: wpcf7mailsent still bubbles as usual, so
 * conversion tracking (gtag_report_conversion(), fbq) runs for agent submissions too.
 */

const TIMEOUT_MS = 30000;

/**
 * Resolves with a text describing CF7's outcome for the submission starting on `form`.
 *
 * Failures resolve too (prefixed "Échec"): a rejected promise only reaches the agent as a
 * generic "respondWith promise was rejected", which would hide CF7's message and invalid fields.
 */
function cf7Outcome(form) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      form.removeEventListener("wpcf7submit", onSubmit);
      resolve(
        "Échec : le formulaire n'a pas répondu à temps. La demande n'a peut-être pas été envoyée.",
      );
    }, TIMEOUT_MS);

    // wpcf7submit fires after every CF7 response, whatever the status.
    function onSubmit(event) {
      clearTimeout(timer);
      form.removeEventListener("wpcf7submit", onSubmit);

      const { status, apiResponse = {} } = event.detail ?? {};
      const message = apiResponse.message || status || "";

      if (status === "mail_sent") {
        deferNextReset(form);
        resolve(`Succès : ${message}`);
        return;
      }

      const invalid = (apiResponse.invalid_fields ?? []).map(
        (f) => `- ${f.field} : ${f.message}`,
      );
      resolve(["Échec : " + message, ...invalid].join("\n"));
    }

    form.addEventListener("wpcf7submit", onSubmit);
  });
}

/**
 * CF7 calls form.reset() synchronously right after wpcf7submit when the mail is sent.
 * Chrome cancels a running tool on form reset ("Tool execution cancelled by a form reset"),
 * and that happens before it sees our resolved promise. Push that one reset to the next task,
 * after the result has reached the browser. CF7's reset handling then runs as usual.
 */
function deferNextReset(form) {
  form.reset = () => {
    delete form.reset; // back to HTMLFormElement.prototype.reset
    setTimeout(() => form.reset(), 0);
  };
}

// Guard: every page bundle includes this module; register the listener once.
if (!window.chocoWebmcpCf7) {
  window.chocoWebmcpCf7 = true;

  // Bubble phase on document: runs after CF7's own listener on the form, which has
  // already called preventDefault() (required before respondWith()) and started its request.
  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (
      !event.agentInvoked ||
      typeof event.respondWith !== "function" ||
      !event.defaultPrevented || // CF7 didn't take over: let the native submission happen
      !(form instanceof HTMLFormElement) ||
      !form.matches(".wpcf7-form[toolname]")
    ) {
      return;
    }
    event.respondWith(cf7Outcome(form));
  });
}
